import { useState, useEffect, useRef } from 'react';

import { generateClassName } from '@klv/shared/utils/functions';
import { Wrapper } from '@klv/utils/wrapper';
import { getBackgroundType } from '@klv/utils/wrapper/functions';

import { useTabIntersectionObserver } from './hooks/useTabIntersectionObserver';
import { useTabScrolling } from './hooks/useTabScrolling';
import { Header } from './components/header';
import { Tabs } from './components/tabs';
import { Slide } from './components/slide';

import * as styles from './styles/scrolljackingSlider.module.css';

import type { IScrolljackingSlider } from './types';

export const ScrolljackingSlider = ({
  className,
  backgroundColor,
  buttons,
  headline,
  headlineStyle = 'display3',
  headlineTag,
  subtext,
  subtextFontStyle,
  subtextTag,
  customPadding = { customPaddingBottom: 'md', customPaddingTop: 'md' },
  scrolljackingSlides = []
}: IScrolljackingSlider) => {
  const [activeCard, setActiveCard] = useState(0);
  const contentItemsRef = useRef<HTMLDivElement>(null);
  const tabButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const anchorRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const backgroundType = getBackgroundType(backgroundColor);
  
  // Use scrolljackingSlides data
  const tabs = scrolljackingSlides.map(slide => ({ label: slide.label || '' }));

  const { isTabClickRef } = useTabIntersectionObserver(setActiveCard, scrolljackingSlides.length, anchorRefs);
  
  // Function to set anchor refs
  const setAnchorRef = (index: number) => (element: HTMLAnchorElement | null) => {
    anchorRefs.current[index] = element;
  };
  
  useTabScrolling(activeCard, tabButtonRefs, tabsContainerRef, anchorRefs);

  useEffect(() => {
    if (contentItemsRef.current) {
      contentItemsRef.current.style.transform = `translateX(-${activeCard * 100}vw)`;
    }
  }, [activeCard]);

  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleTabClick = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.preventDefault();
    
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Set flag to disable intersection observer
    isTabClickRef.current = true;
    
    // Update active card
    setActiveCard(index);
    
    // Re-enable intersection observer after scroll completes
    scrollTimeoutRef.current = setTimeout(() => {
      isTabClickRef.current = false;
    }, 800);
  };

  // If no slides are provided, return null
  if (scrolljackingSlides.length === 0) {
    console.error('No slides provided for ScrolljackingSlider');
    return null;
  }

  return (
    <Wrapper contained={false} customPadding={customPadding} backgroundColor={backgroundColor} fadeIn={0}>
      <Header
        headline={headline}
        headlineStyle={headlineStyle}
        headlineTag={headlineTag}
        subtext={subtext}
        subtextFontStyle={subtextFontStyle}
        subtextTag={subtextTag}
        buttons={buttons}
        backgroundType={backgroundType}
      />
      <div className={generateClassName([styles.scrolljackingSlider, className])}>
        <div className={styles.contentViewport}>
          <Tabs
            tabs={tabs}
            activeCard={activeCard}
            tabButtonRefs={tabButtonRefs}
            tabsContainerRef={tabsContainerRef}
            onTabClick={handleTabClick}
          />
          <div className={styles.contentWrapper}>
            <div className={styles.contentItems} ref={contentItemsRef}>
              {scrolljackingSlides.map((slide, index) => (
                <div key={index}>
                  <Slide
                    slide={slide}
                    customPadding={{ customPaddingBottom: 'xs', customPaddingTop: 'xs' }}
                    backgroundType={backgroundType}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dynamic anchor elements for scroll positioning */}
        {scrolljackingSlides.map((_, index) => (
          <a key={index} ref={setAnchorRef(index)} className={styles.contentAnchor} />
        ))}
      </div>
    </Wrapper>
  );
};
